import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { uploadV2ApiService } from '../services/uploadV2ApiService';
import { toast } from 'react-hot-toast';
import { AlertTriangle, X } from 'lucide-react';
import AISettingsV2 from '../components/upload-v2/AISettingsV2';
import { AISettingsData, loadAISettings, saveAISettings, resetAISettings } from '../utils/aiSettingsStorage';
import { calculateRequiredCredits } from '../utils/creditCalculation';
import { useUserData } from '../hooks/useUserData';
import { validateFilesBatch } from '../utils/fileValidation';
import { UploadFile } from '../hooks/upload-v2/useUploadV2Album';
import { createClient } from '@supabase/supabase-js';

// Utility function to truncate filename for display
const truncateFilename = (filename: string, maxLength: number = 28): string => {
  if (filename.length <= maxLength) return filename;
  
  const extension = filename.split('.').pop() || '';
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  
  if (extension.length + 3 >= maxLength) {
    return filename.substring(0, maxLength - 3) + '...';
  }
  
  const availableLength = maxLength - extension.length - 4; // 4 for "..." and "."
  return nameWithoutExt.substring(0, availableLength) + '...' + '.' + extension;
};

// Extend UploadFile interface to include thumbnail URL
interface ExtendedUploadFile extends UploadFile {
  thumbnailUrl?: string;
  metadata?: {
    title?: string;
    description?: string;
    keywords?: string[];
  };
}

// Progress file interface for type safety
interface ProgressFile {
  fileId: string;
  status: string;
  progress?: number;
  errorMessage?: string;
  metadata?: {
    title?: string;
    description?: string;
    keywords?: string[];
  };
  thumbnailUrl?: string;
}

// WebSocket message types
interface WebSocketMessage {
  sessionId: string;
  type: 'SESSION_UPDATE' | 'FILE_UPDATE' | 'BATCH_COMPLETE';
  data: any;
  timestamp: string;
}

interface ErrorMessage {
  id: string;
  message: string;
  type: 'error' | 'warning';
}

const UploadV2Page: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<ExtendedUploadFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [sessionId, setSessionId] = useState<string | null>(null); // TODO: Use for session management
  const [isCreditValidating, setIsCreditValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressInterval, setProgressInterval] = useState<number | null>(null);
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sendEmailNotification, setSendEmailNotification] = useState(false); // Email notification preference
  
  // Initialize Supabase client ONCE (memoized, will never change)
  const supabaseClient = useMemo(
    () => createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    ),
    [] // Empty deps = create once only
  );
  
  // Wrapper function to log email notification changes
  const handleEmailNotificationChange = (enabled: boolean) => {
    setSendEmailNotification(enabled);
  };
  
  // Track actual processing times for better estimates
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [fileProcessingTimes, setFileProcessingTimes] = useState<{ [fileId: string]: { start: number; end?: number } }>({}); // TODO: Use for processing time analytics
  const [avgProcessingTime, setAvgProcessingTime] = useState<number>(12); // Default: 12 seconds (realistic for Indo → US)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null); // TODO: Use for upload time tracking

  // 🌐 Supabase Realtime state for real-time updates (Phase 3)
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const realtimeChannelRef = useRef<any>(null);
  const ENABLE_REALTIME = import.meta.env.VITE_ENABLE_REALTIME !== 'false'; // Default: enabled

  // 💫 CRITICAL: Clean up old album localStorage entries (V2 SEPARATE from V1)
  useEffect(() => {
  // ...existing code...
    
    // Clean up old non-album-specific storage (legacy)
    localStorage.removeItem('pendingUploadData');
    
    // V2: Use separate localStorage keys to avoid interference with V1
    const activeAlbums = JSON.parse(localStorage.getItem('activeAlbumsV2') || '[]');
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    const validAlbums: string[] = [];
    
    activeAlbums.forEach((albumSlug: string) => {
      const albumKey = `pendingUploadDataV2_${albumSlug}`;
      const albumData = localStorage.getItem(albumKey);
      
      if (albumData) {
        try {
          const parsed = JSON.parse(albumData);
          const age = now - (parsed.timestamp || 0);
          
          if (age > maxAge) {
            // Remove old album data
            localStorage.removeItem(albumKey);
          } else {
            // Keep valid album
            validAlbums.push(albumSlug);
          }
        } catch (error) {
          // Remove corrupted data
          localStorage.removeItem(albumKey);
        }
      }
    });
    
    // Update active albums list for V2
    localStorage.setItem('activeAlbumsV2', JSON.stringify(validAlbums));
  }, []); // Only run once on component mount

  // V2: Album creation state
  const [albumCreated, setAlbumCreated] = useState(false);
  const [currentAlbumSlug, setCurrentAlbumSlug] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [albumTitle, setAlbumTitle] = useState<string>(''); // TODO: Use in future UI features

  // AI Settings state - LOAD FROM SESSION STORAGE (like V1)
  const [aiSettings, setAiSettings] = useState<AISettingsData>(() => loadAISettings());

  // File Display Management - Simplified: Only "All" and "Completed" tabs (no "Failed" tab)
  const [fileDisplayTab, setFileDisplayTab] = useState<'all' | 'completed'>('all');

  // Handle AI Settings reset (like V1) - CLEAR SESSION STORAGE
  const handleResetAISettings = useCallback(() => {
    const defaultSettings = resetAISettings();
    setAiSettings(defaultSettings);
    toast.success('AI settings reset to defaults');
  }, []);

  // SAVE AI SETTINGS TO SESSION STORAGE ON CHANGE (like V1)
  useEffect(() => {
    saveAISettings(aiSettings);
  }, [aiSettings]);

  // Use the SAME REUSABLE hook - NO MORE DUPLICATE HOOKS!
  const { userInfo } = useUserData();

  // File Display Logic - Simplified (No "Failed" tracking)

  const getFileStats = useCallback(() => {
    const total = files.length;
    const completed = files.filter(f => f.status === 'completed').length;
    const processing = files.filter(f => f.status === 'processing').length;
    const pending = files.filter(f => f.status === 'pending').length;
    const uploading = files.filter(f => f.status === 'uploading').length;
    const inProgress = processing + pending + uploading; // All files still being worked on

    return { total, completed, inProgress, processing, pending, uploading };
  }, [files]);

  const getFilteredFiles = useCallback(() => {
    switch (fileDisplayTab) {
      case 'completed':
        return files.filter(f => f.status === 'completed').slice(-10); // Most recent 10
      case 'all':
        return files.slice(0, 50); // First 50, with pagination potential
      default:
        return [];
    }
  }, [files, fileDisplayTab]);

  // V2 Album Creation Hook
  // V2: No separate album creation - backend creates album with files
  const availableCredits = userInfo?.credits || 0;

  // Calculate required credits based on file types
  const requiredCredits = calculateRequiredCredits(files.map(uploadedFile => ({
    name: uploadedFile.file.name,
    type: uploadedFile.file.type,
    size: uploadedFile.file.size
  })));

  // Credit validation states
  const hasEnoughCredits = availableCredits >= requiredCredits;

  // Membership validation
  // const hasActiveMembership = userInfo?.current_package && userInfo?.membership_expires_at && new Date(userInfo.membership_expires_at) > new Date(); // Not used
  // const isFreeAccount = userInfo?.free_account_status || false; // User who received 10 credits one-time bonus - Not used
  const membershipExpired = userInfo?.current_package && userInfo?.membership_expires_at && new Date(userInfo.membership_expires_at) <= new Date();

  // Enhanced file validation using V1 rules

  // Handle file selection with V1 validation rules + DUPLICATE DETECTION
  const handleFiles = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const fileArray = Array.from(selectedFiles);

    // Get existing files for duplicate detection
    const existingFiles = files.map(f => f.file);

    try {
      // Use V1 batch validation with duplicate detection
      const batchValidation = await validateFilesBatch(fileArray, existingFiles);

      const newErrorMessages: ErrorMessage[] = [];

      // Handle batch errors (too many files)
      if (batchValidation.batchErrors.length > 0) {
        batchValidation.batchErrors.forEach(error => {
          newErrorMessages.push({
            id: `batch-error-${Date.now()}-${Math.random()}`,
            message: error,
            type: 'error'
          });
        });
      }

      // Handle invalid files
      if (batchValidation.invalidFiles.length > 0) {
        batchValidation.invalidFiles.forEach(({ file, errors }) => {
          errors.forEach(error => {
            newErrorMessages.push({
              id: `file-error-${Date.now()}-${Math.random()}`,
              message: `${file.name}: ${error}`,
              type: 'error'
            });
          });
        });
      }

      // Handle duplicate files (V1 behavior: notification only, no error)
      if (batchValidation.duplicateFiles.length > 0) {
        batchValidation.duplicateFiles.forEach(duplicateFile => {
          newErrorMessages.push({
            id: `duplicate-${Date.now()}-${Math.random()}`,
            message: `⚠️ Duplicate detected: ${duplicateFile.name}. Only one copy has been kept, the rest were removed.`,
            type: 'warning'
          });
        });
      }

      // Handle files with warnings
      if (batchValidation.filesWithWarnings.length > 0) {
        batchValidation.filesWithWarnings.forEach(({ file, warnings }) => {
          warnings.forEach(warning => {
            newErrorMessages.push({
              id: `warning-${Date.now()}-${Math.random()}`,
              message: `${file.name}: ${warning}`,
              type: 'warning'
            });
          });
        });
      }

      // Update error messages state (like V1)
      setErrorMessages(prev => [...prev, ...newErrorMessages]);

      // Convert valid files to UploadFile format and add to state
      if (batchValidation.validFiles.length > 0) {
        const newUploadFiles: ExtendedUploadFile[] = batchValidation.validFiles.map(file => ({
          id: Math.random().toString(36).substr(2, 9),
          file,
          status: 'pending',
          progress: 0
        }));

        setFiles(prev => [...prev, ...newUploadFiles]);
        toast.success(`${batchValidation.validFiles.length} file(s) added successfully`);
      }

    } catch (error) {
      const errorMessage: ErrorMessage = {
        id: `validation-error-${Date.now()}`,
        message: `File validation failed: ${error}`,
        type: 'error'
      };
      setErrorMessages(prev => [...prev, errorMessage]);
      console.error('Batch validation error:', error);
    }
  }, [files]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  // File input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  // V2: Upload files to S3 using presigned URLs with concurrent processing for large batches
  const uploadFilesToS3 = useCallback(async (uploads: Array<{
    fileId: string;
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    expiresIn: number;
  }>, currentSessionId: string) => {
    
    // Concurrent upload settings for large batches (up to 500+ files)
    const MAX_CONCURRENT_UPLOADS = 20; // Reasonable concurrent S3 uploads
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds


    // Helper function to process a single file with retry logic
    const processFileWithRetry = async (uploadData: {
      fileId: string;
      fileName: string;
      presignedUrl: string;
      s3Key: string;
      expiresIn: number;
    }, retryCount = 0): Promise<void> => {
      const file = files.find(f => f.id === uploadData.fileId);
      if (!file) {
        console.error('❌ File not found for upload:', uploadData.fileId);
        console.error('🔍 Available frontend file IDs:', files.map(f => f.id));
        return;
      }

      try {
        // Update file status to uploading
        setFiles(prev => prev.map(f =>
          f.id === uploadData.fileId
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        ));

        // Upload file using uploadV2ApiService
        await uploadV2ApiService.uploadFileToS3(
          file.file,
          uploadData.presignedUrl,
          {}, // No additional fields needed for simple presigned URL
          (progress) => {
            // Update progress
            setFiles(prev => prev.map(f =>
              f.id === uploadData.fileId
                ? { ...f, progress }
                : f
            ));
          }
        );

        // V2: Confirm upload completion with retry logic
        let confirmSuccess = false;
        let confirmError: Error | null = null;
        
        for (let confirmRetry = 0; confirmRetry <= MAX_RETRIES; confirmRetry++) {
          try {
            await uploadV2ApiService.confirmUpload({
              sessionId: currentSessionId,
              fileId: uploadData.fileId,
              s3Key: uploadData.s3Key
            });
            confirmSuccess = true;
            break;
          } catch (error) {
            confirmError = error instanceof Error ? error : new Error(String(error));
            console.warn(`⚠️ Upload confirmation failed for ${file.file.name} (attempt ${confirmRetry + 1}/${MAX_RETRIES + 1}):`, error);
            
            if (confirmRetry < MAX_RETRIES) {
              // Wait before retry with exponential backoff
              const delay = RETRY_DELAY * Math.pow(2, confirmRetry);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }

        if (!confirmSuccess) {
          console.error('❌ Upload confirmation failed after all retries:', file.file.name, confirmError);
          throw confirmError;
        }

        // Update file status to processing (SQS will handle the rest)
        setFiles(prev => prev.map(f =>
          f.id === uploadData.fileId
            ? { ...f, status: 'processing' as const, progress: 100 }
            : f
        ));

      } catch (error) {
        console.error(`❌ File upload failed for ${file.file.name} (attempt ${retryCount + 1}):`, error);

        // Retry logic for entire upload process
        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY * Math.pow(2, retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return processFileWithRetry(uploadData, retryCount + 1);
        }

        // Update file status to failed after all retries
        setFiles(prev => prev.map(f =>
          f.id === uploadData.fileId
            ? { ...f, status: 'failed' as const, error: error instanceof Error ? error.message : 'Upload failed after retries' }
            : f
        ));
      }
    };

    // Concurrency control using a semaphore-like approach
    const runConcurrent = async (tasks: Array<() => Promise<void>>, maxConcurrent: number) => {
      const results: Promise<void>[] = [];
      const executing: Promise<void>[] = [];

      for (const task of tasks) {
        const promise = task().then(() => {
          executing.splice(executing.indexOf(promise), 1);
        });
        
        results.push(promise);
        executing.push(promise);

        if (executing.length >= maxConcurrent) {
          await Promise.race(executing);
        }
      }

      await Promise.allSettled(results);
    };

    // Create tasks for all uploads
    const uploadTasks = uploads.map(uploadData => 
      () => processFileWithRetry(uploadData)
    );

    // Execute all uploads with concurrency control
    await runConcurrent(uploadTasks, MAX_CONCURRENT_UPLOADS);
    
  }, [files]);

  // 🌐 Supabase Realtime: Subscribe to database changes (Phase 3)
  const subscribeToRealtimeUpdates = useCallback((sessionId: string) => {
    if (!ENABLE_REALTIME) {
      console.log('⚠️ Realtime disabled, using polling only');
      return;
    }

    // Don't resubscribe if already subscribed
    if (realtimeChannelRef.current) {
      console.log('✅ Realtime already subscribed');
      return;
    }

    try {
      console.log('🔌 Subscribing to Realtime updates for session:', sessionId);
      
      // Subscribe to upload_session_files table changes for this session
      const channel = supabaseClient
        .channel(`upload-session-${sessionId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'upload_session_files',
            filter: `session_id=eq.${sessionId}`
          },
          (payload) => {
            console.log('📨 Realtime update received:', payload);
            
            const updatedFile = payload.new as any;
            
            // Update file in state (single setState to avoid warning)
            setFiles(prev => {
              const updated = prev.map(file => {
                if (file.id === updatedFile.file_id) {
                  const newStatus = updatedFile.status.toLowerCase();
                  
                  console.log(`📝 Updating file ${file.file.name}: ${file.status} → ${newStatus}`);
                  
                  return {
                    ...file,
                    status: newStatus as any,
                    error: updatedFile.error_message || undefined,
                    metadata: file.metadata, // Keep existing metadata
                    thumbnailUrl: file.thumbnailUrl // Keep existing thumbnail
                  };
                }
                return file;
              });

              // Check if all files completed or failed (processing finished)
              const allFinished = updated.every(f => 
                f.status === 'completed' || f.status === 'failed'
              );
              const allCompleted = updated.every(f => f.status === 'completed');
              
              if (allFinished) {
                if (allCompleted) {
                  console.log('🎉 All files completed via Realtime!');
                } else {
                  console.log('⚠️ Some files failed, but processing finished via Realtime');
                }
                
                // Don't call toast here - it causes setState during render warning
                // Completion toast will be handled by useEffect watching files state
                
                // Cleanup Realtime subscription
                if (realtimeChannelRef.current) {
                  supabaseClient.removeChannel(realtimeChannelRef.current);
                  realtimeChannelRef.current = null;
                }
              }
              
              return updated;
            });
          }
        )
        .subscribe((status) => {
          console.log('🔌 Realtime subscription status:', status);
          setRealtimeConnected(status === 'SUBSCRIBED');
        });

      realtimeChannelRef.current = channel;

    } catch (error) {
      console.error('❌ Failed to subscribe to Realtime:', error);
      setRealtimeConnected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ENABLE_REALTIME]); // supabase is now a ref, no need in deps

  // Cleanup Realtime on unmount ONLY (not on re-render)
  useEffect(() => {
    return () => {
      if (realtimeChannelRef.current) {
        console.log('🔌 Cleaning up Realtime subscription on unmount');
        supabaseClient.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = cleanup only on unmount

  // V2: Start progress tracking (hybrid: Supabase Realtime primary, polling fallback)
  const startProgressTracking = useCallback((sessionId: string) => {
    // Set upload start time for overall progress calculation
    setUploadStartTime(Date.now());

    // 🌐 PHASE 3: Subscribe to Supabase Realtime (real-time updates from database!)
    if (ENABLE_REALTIME) {
      console.log('🚀 Starting Supabase Realtime subscription...');
      subscribeToRealtimeUpdates(sessionId);
      
      // Still start polling as fallback (slower interval)
      // If Realtime works, polling becomes backup safety net
      console.log('🔄 Starting polling as fallback (Realtime primary)');
    } else {
      console.log('🔄 Realtime disabled, using polling only');
    }

    // ⭐ ADAPTIVE POLLING: Adjust interval based on queue depth
    // NOTE: With Realtime, polling is just a fallback safety net
    let currentInterval = ENABLE_REALTIME ? 30000 : 10000; // 30s with Realtime, 10s without
    
    const pollProgress = async () => {
      try {
        const progress = await uploadV2ApiService.getProgress(sessionId);

        // ⭐ ADAPTIVE INTERVAL: Based on pending files and processing state
        if (progress.stats) {
          const pendingFiles = progress.stats.pending;
          const processingFiles = progress.stats.processing;
          
          if (pendingFiles > 100) {
            currentInterval = 30000; // 30 seconds for large queue (very santai!)
          } else if (pendingFiles > 10) {
            currentInterval = 15000; // 15 seconds for medium queue
          } else if (pendingFiles > 0 || processingFiles > 0) {
            currentInterval = 3000;  // 3 seconds for small queue or active processing (faster!)
          } else {
            currentInterval = 2000;  // 2 seconds when finishing up (very fast!)
          }
        }

        // Update file statuses based on progress response
        if (progress.files) {
          setFiles(prev => prev.map(file => {
            const progressFile = progress.files.find(pf => pf.fileId === file.id);
            if (progressFile) {
              // ...existing code...
              const typedProgressFile = progressFile as ProgressFile;
              
              // Track processing times for better estimates
              const newStatus = progressFile.status.toLowerCase();
              const oldStatus = file.status;
              
              // 🛡️ HANDLE BACKEND FAILURES GRACEFULLY
              // If backend marks as "failed", check error message
              if (newStatus === 'failed' && typedProgressFile.errorMessage) {
                console.error(`❌ Backend reported failure for ${file.file.name}:`, typedProgressFile.errorMessage);
                
                // Check if this is a PERMANENT failure (DLQ) vs temporary retry
                const isPermanentFailure = typedProgressFile.errorMessage.includes('after all retries') ||
                                          typedProgressFile.errorMessage.includes('permanently failed') ||
                                          typedProgressFile.errorMessage.includes('DLQ');
                
                if (isPermanentFailure) {
                  // Show user-friendly notification (only once per file)
                  if (!file.error || !file.error.includes('team has been notified')) {
                    toast.error(
                      `We encountered an issue processing "${file.file.name.substring(0, 30)}...". ` +
                      `Our team has been notified and will investigate. Your album: ${currentAlbumSlug}`,
                      { duration: 8000 }
                    );
                  }
                  
                  // Keep showing as "processing" in UI (no scary red X)
                  // Admin will handle manually via DLQ
                  return {
                    ...file,
                    status: 'processing' as const, // Still show blue spinner
                    progress: typedProgressFile.progress || 50,
                    error: '⚠️ Issue detected - team has been notified',
                    metadata: typedProgressFile.metadata || file.metadata,
                    thumbnailUrl: typedProgressFile.thumbnailUrl || file.thumbnailUrl
                  };
                } else {
                  // Temporary failure - backend will retry
                  // Keep showing as processing, backend handles retry automatically
                  return {
                    ...file,
                    status: 'processing' as const, // Show as processing (backend retrying)
                    progress: typedProgressFile.progress || 30,
                    error: undefined, // Don't show error to user (internal retry)
                    metadata: typedProgressFile.metadata || file.metadata,
                    thumbnailUrl: typedProgressFile.thumbnailUrl || file.thumbnailUrl
                  };
                }
              }
              
              // When file starts processing, record start time
              if (newStatus === 'processing' && oldStatus !== 'processing') {
                setFileProcessingTimes(prev => ({
                  ...prev,
                  [file.id]: { start: Date.now() }
                }));
              }
              
              // When file completes, calculate processing time and update average
              if (newStatus === 'completed' && (oldStatus === 'processing' || oldStatus === 'pending')) {
                setFileProcessingTimes(prev => {
                  const updated = {
                    ...prev,
                    [file.id]: { ...prev[file.id], end: Date.now() }
                  };
                  
                  // Calculate average processing time from all completed files
                  const completedTimes = Object.values(updated)
                    .filter(t => t.start && t.end) // Make sure both start and end exist
                    .map(t => (t.end! - t.start) / 1000); // Convert to seconds
                  
                  if (completedTimes.length > 0) {
                    const avg = completedTimes.reduce((sum, t) => sum + t, 0) / completedTimes.length;
                    setAvgProcessingTime(Math.ceil(avg));
                  }
                  
                  return updated;
                });
              }
              
              return {
                ...file,
                status: newStatus as 'pending' | 'uploading' | 'processing' | 'completed' | 'failed',
                progress: typedProgressFile.progress || 0,
                error: typedProgressFile.errorMessage || undefined,
                metadata: typedProgressFile.metadata || file.metadata, // Update metadata
                thumbnailUrl: typedProgressFile.thumbnailUrl || file.thumbnailUrl // Update thumbnail URL
              };
            }
            return file;
          }));
        }

        // Check if all files are completed
        const allCompleted = progress.files?.every(f => f.status === 'completed');
        if (allCompleted) {
          clearInterval(progressInterval);

          // Show completion message
          toast.success('All files processed successfully!');

          // TODO: Add option to redirect to results page
          return; // Stop polling
        }

        // ⭐ SCHEDULE NEXT POLL with adaptive interval
        setTimeout(pollProgress, currentInterval);

      } catch (error) {
        console.error('❌ Progress tracking error:', error);
        // On error, retry with longer interval (don't hammer the server!)
        setTimeout(pollProgress, 15000);
      }
    };

    // Start first poll immediately
    pollProgress();

    // Store a dummy interval for cleanup (we're using setTimeout instead)
    const progressInterval = setInterval(() => {}, 999999) as unknown as number;
    setProgressInterval(progressInterval);
  }, [currentAlbumSlug]);

  // Perform actual upload after all validations pass (V2 Style: Album Creation First)
  const performActualUpload = useCallback(async () => {

    try {
      // V2: Call initiateUpload directly (backend creates album with files)

      // SECURITY: Read file signatures for backend validation
      const { readFileSignature, readSvgAsBase64 } = await import('../utils/fileSignature');
      
      const fileData = await Promise.all(files.map(async (f) => {
        try {
          // Read first 12 bytes (magic bytes) for MIME type verification
          const signature = await readFileSignature(f.file, 12);
          
          // For SVG files, also read full content for sanitization
          let svgData: string | undefined;
          if (f.file.type === 'image/svg+xml') {
            svgData = await readSvgAsBase64(f.file);
          }
          
          return {
            fileId: f.id,
            fileName: f.file.name,
            fileSize: f.file.size,
            mimeType: f.file.type,
            fileSignature: signature, // Magic bytes for backend validation
            fileData: svgData // SVG content for sanitization (only for SVG)
          };
        } catch (error) {
          console.error(`Failed to read signature for ${f.file.name}:`, error);
          throw new Error(`Security validation failed for ${f.file.name}`);
        }
      }));

  // ...existing code...

      // Call initiate upload API
  const response = await uploadV2ApiService.initiateUpload({
    files: fileData,
    settings: {
      maxTitleLength: aiSettings.title_length,
      maxDescriptionLength: aiSettings.description_length,
      maxKeywords: aiSettings.keywords_count
    },
    customInstruction: aiSettings.custom_instruction,
    sendEmailNotification // Pass email notification preference to backend
  });

  if (!response?.success || !response.sessionId) {
    throw new Error(response?.message || 'Failed to initiate upload session');
  }

  // V2: Load album data from database to get proper album name
  try {
    const albumData = await uploadV2ApiService.getAlbumData(response.albumSlug!);
    setAlbumTitle(albumData.albumName || response.albumSlug!);
  } catch (error) {
    console.warn('⚠️ V2: Failed to load album data from database, using slug:', error);
    setAlbumTitle(response.albumSlug!);
  }

  // V2: Set album state and hide forms
  setAlbumCreated(true);
  setCurrentAlbumSlug(response.albumSlug!);
  setSessionId(response.sessionId!);

  // V2: STAY on upload page for real-time progress tracking

  // PHASE 3: Upload files to S3 using presigned URLs
  await uploadFilesToS3(response.uploads, response.sessionId!);

  // PHASE 4: Start progress tracking
  startProgressTracking(response.sessionId!);

  // V2: No redirect - stay on upload page for progress tracking

  } catch (error: unknown) {
    toast.error(error instanceof Error ? error.message : 'Failed to start upload');
    console.error('Upload error:', error);
  } finally {
    // Upload process completed
  }
  }, [files, aiSettings, startProgressTracking, uploadFilesToS3, sendEmailNotification]);

  // Start upload process - DIRECT UPLOAD (V2 Style: No Modal)
  const startUpload = useCallback(async () => {
    // Prevent double execution
    if (isProcessing) {
      return;
    }

    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    // SECURITY: Re-calculate credits to prevent manipulation
    const currentRequiredCredits = calculateRequiredCredits(files.map(uploadedFile => ({
      name: uploadedFile.file.name,
      type: uploadedFile.file.type,
      size: uploadedFile.file.size
    })));
    const currentAvailableCredits = userInfo?.credits || 0;
    const currentHasEnoughCredits = currentAvailableCredits >= currentRequiredCredits;

    // LAYER 1: FRONTEND VALIDATION - BLOCK IMMEDIATELY IF OBVIOUSLY INVALID
    if (!currentHasEnoughCredits || membershipExpired) {
      toast.error('Insufficient credits for this upload');
      return;
    }

    // V2: DIRECT UPLOAD - No modal, straight to upload
    setIsProcessing(true);
    setIsCreditValidating(true);

    try {
      await performActualUpload();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setIsCreditValidating(false);
    }
  }, [files, userInfo, membershipExpired, isProcessing, performActualUpload]);


  // Error message management (like V1)
  // File management handlers
  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
  }, []);

  const handleClearErrors = useCallback(() => {
    setErrorMessages([]);
  }, []);

  const handleRemoveError = useCallback((errorId: string) => {
    setErrorMessages(prev => prev.filter(error => error.id !== errorId));
  }, []);


  // Cleanup progress tracking on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);


  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          
          {/* Desktop App Promo Banner - Non-intrusive */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Need to process more images and videos? Try our Desktop App
                  </p>
                  <p className="text-xs text-gray-600">
                    Process larger batches with better performance • Available for macOS & Windows
                  </p>
                </div>
              </div>
              <a
                href="/desktop"
                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </a>
            </div>
          </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {/* Main Content - Responsive: full width on mobile, 8 cols on desktop */}
            <div className={albumCreated ? 'lg:col-span-8 lg:col-start-3' : 'lg:col-span-8'}>
              {/* File Selection & Upload Section */}
              {!albumCreated && (
                <div>
                  {/* No file display before album creation */}
                </div>
              )}

              {/* File Progress Dashboard - NEW SCALABLE UX */}
              {albumCreated && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                  {/* Progress Overview */}
                  <div className="mb-6">
                    {/* Simple Processing Indicator */}
                    <div className="space-y-4">
                      {/* Conditional: Processing or Completed */}
                      {(() => {
                        const stats = getFileStats();
                        const isAllCompleted = stats.completed === stats.total && stats.total > 0;
                        
                        if (isAllCompleted) {
                          // ✅ COMPLETION STATE - Beautiful Success Message
                          return (
                            <div className="animate-fadeIn">
                              {/* Success Icon with Animation */}
                              <div className="flex items-center justify-center py-8">
                                <div className="relative">
                                  {/* Animated Success Checkmark */}
                                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center animate-scaleIn shadow-lg">
                                    <svg className="w-12 h-12 text-white animate-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                  {/* Pulsing Ring Effect */}
                                  <div className="absolute inset-0 w-20 h-20 border-4 border-green-400 rounded-full animate-ping opacity-20"></div>
                                </div>
                              </div>

                              {/* Success Message */}
                              <div className="text-center space-y-4">
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent animate-slideUp">
                                  ✨ METAPIC MAGIC IS DONE!
                                </h2>
                                <p className="text-lg text-gray-700 animate-slideUp animation-delay-100">
                                  Processing your {stats.total} {stats.total === 1 ? 'file' : 'files'} is complete. Enjoy! 🎉
                                </p>

                                {/* CTA Button */}
                                <div className="pt-6 animate-slideUp animation-delay-200">
                                  <a
                                    href={`/results-v2/${currentAlbumSlug}`}
                                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300"
                                  >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    <span className="text-lg">View Album & Export CSV</span>
                                    <svg className="w-5 h-5 animate-bounce-horizontal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </a>
                                  <p className="text-sm text-gray-500 mt-4">
                                    💡 Edit metadata, regenerate AI content, and export CSV
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // ⏳ PROCESSING STATE - Animated Loader
                        return (
                          <>
                            {/* Magic Message with Animated Loader */}
                            <div className="flex items-center justify-center gap-3 py-8">
                              <div className="relative">
                                {/* Animated Spinner */}
                                <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                                {/* Pulsing Glow Effect */}
                                <div className="absolute inset-0 w-12 h-12 border-4 border-purple-400 rounded-full animate-ping opacity-20"></div>
                              </div>
                              <div className="text-center">
                                <h3 className="text-xl font-semibold text-gray-800 mb-1">
                                  ✨ We're Making Magic With Your Files
                                </h3>
                                <p className="text-sm text-gray-600">
                                  AI is analyzing and optimizing your images...
                                </p>
                              </div>
                            </div>

                            {/* Stats Display */}
                            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-100">
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
                                  <div className="text-xs text-gray-600 mt-1">Total Files</div>
                                </div>
                                <div>
                                  <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
                                  <div className="text-xs text-gray-600 mt-1">Completed</div>
                                </div>
                                <div>
                                  <div className="text-3xl font-bold text-blue-600">{stats.inProgress}</div>
                                  <div className="text-xs text-gray-600 mt-1">In Progress</div>
                                </div>
                              </div>

                              {/* Estimated Time */}
                              {(() => {
                                const remainingFiles = stats.inProgress;
                                
                                if (remainingFiles > 0 && stats.completed < stats.total) {
                                  let estimatedSeconds = remainingFiles * avgProcessingTime;
                                  
                                  if (!estimatedSeconds || estimatedSeconds < 0 || !isFinite(estimatedSeconds)) {
                                    estimatedSeconds = remainingFiles * 12;
                                  }
                                  
                                  const minutes = Math.floor(estimatedSeconds / 60);
                                  const seconds = Math.floor(estimatedSeconds % 60);
                                  
                                  return (
                                    <div className="mt-4 pt-4 border-t border-purple-200 text-center">
                                      <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium">
                                          Estimated time: ~{minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} sec`}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-2">
                                        Feel free to grab a coffee ☕ - we'll let you know when it's done!
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Tab Navigation - Simplified: Only "All Files" and "Completed" */}
                  <div className="border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8 overflow-x-auto">
                      {[
                        { key: 'all', label: 'All Files', count: getFileStats().total },
                        { key: 'completed', label: 'Completed', count: getFileStats().completed }
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setFileDisplayTab(tab.key as 'all' | 'completed')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                            fileDisplayTab === tab.key
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {tab.label}
                          {tab.count !== null && tab.count > 0 && (
                            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                              tab.key === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {tab.count}
                            </span>
                          )}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Tab Content */}
                  <div className="min-h-[200px]">


                    {fileDisplayTab === 'completed' && (
                      <div className="space-y-3">
                        {getFilteredFiles().length > 0 ? (
                          getFilteredFiles().map((file) => (
                        <div key={file.id} className="border border-green-200 rounded-lg p-4 bg-green-50">
                          <div className="flex items-start gap-4">
                            {/* Thumbnail and Filename - Left Side */}
                            <div className="flex-shrink-0 flex flex-col items-center gap-2">
                              {/* Thumbnail */}
                              {file.thumbnailUrl ? (
                                <img
                                  src={file.thumbnailUrl}
                                  alt={file.file.name}
                                  className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                />
                              ) : (
                                <div className="w-20 h-20 bg-gray-200 rounded-lg border border-gray-200 flex items-center justify-center">
                                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}

                              {/* Filename */}
                              <div className="text-center">
                                <div className="text-xs font-medium text-green-800 truncate max-w-[80px]" title={file.file.name}>
                                  {truncateFilename(file.file.name)}
                                </div>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full mt-1 inline-block">
                                  completed
                                </span>
                              </div>
                            </div>

                            {/* Metadata - Right Side */}
                            <div className="flex-1 min-w-0">
                              {/* Metadata Display */}
                              <div className="space-y-3">
                                {file.metadata?.title && (
                                  <div>
                                    <span className="text-sm font-semibold text-gray-800">Title:</span>
                                    <div className="text-sm text-gray-700 mt-1 line-clamp-2">{file.metadata.title}</div>
                                  </div>
                                )}
                                {file.metadata?.description && (
                                  <div>
                                    <span className="text-sm font-semibold text-gray-800">Description:</span>
                                    <div className="text-sm text-gray-700 mt-1 line-clamp-3">{file.metadata.description}</div>
                                  </div>
                                )}
                                {file.metadata?.keywords && file.metadata.keywords.length > 0 && (
                                  <div>
                                    <span className="text-sm font-semibold text-gray-800">Keywords:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {file.metadata.keywords.slice(0, 8).map((keyword, index) => (
                                        <span key={index} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                                          {keyword}
                                        </span>
                                      ))}
                                      {file.metadata.keywords.length > 8 && (
                                        <span className="text-gray-500 text-xs px-2 py-1">+{file.metadata.keywords.length - 8} more</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {(!file.metadata?.title && !file.metadata?.description && (!file.metadata?.keywords || file.metadata.keywords.length === 0)) && (
                                  <div className="text-sm text-gray-500 italic">
                                    Metadata processing...
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <div className="text-2xl mb-2">✅</div>
                            <div>No completed files yet</div>
                          </div>
                        )}
                        {getFileStats().completed > 10 && (
                          <div className="text-center text-sm text-gray-500">
                            ... and {getFileStats().completed - 10} more completed files
                          </div>
                        )}
                      </div>
                    )}

                    {fileDisplayTab === 'all' && (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {getFilteredFiles().map((file) => {
                          // Determine detailed status stage (NO "FAILED" STATE - backend handles retries)
                          const getDetailedStatus = () => {
                            if (file.status === 'completed') return { stage: 'completed', text: 'Completed', color: 'green' };
                            if (file.status === 'pending') return { stage: 'pending', text: 'Pending', color: 'gray' };
                            if (file.status === 'uploading') return { stage: 'uploading', text: 'Uploading', color: 'blue' };
                            
                            // Processing stage - check if metadata exists
                            if (file.status === 'processing') {
                              if (file.metadata && file.metadata.title) {
                                return { stage: 'finalizing', text: 'Finalizing', color: 'blue' };
                              }
                              return { stage: 'generating', text: 'Generating Metadata', color: 'blue' };
                            }
                            
                            // Default: treat unknown status as "Processing" (backend will handle retries)
                            return { stage: 'processing', text: 'Processing', color: 'blue' };
                          };

                          const status = getDetailedStatus();

                          return (
                            <div key={file.id} className={`p-3 rounded-lg border ${
                              status.color === 'green' ? 'border-green-200 bg-green-50' :
                              status.color === 'blue' ? 'border-blue-200 bg-blue-50' :
                              'border-gray-200 bg-gray-50'
                            }`}>
                              {/* Top row: filename and status badge */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  {/* Status icon - spinning for processing states */}
                                  {status.stage === 'completed' ? (
                                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  ) : status.stage === 'pending' ? (
                                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    // Spinning loader for uploading/processing/generating/finalizing
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full flex-shrink-0"></div>
                                  )}
                                  
                                  <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-medium truncate" title={file.file.name}>{truncateFilename(file.file.name)}</span>
                                    <span className="text-xs text-gray-500">
                                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                  </div>
                                </div>
                                
                                <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ml-2 ${
                                  status.color === 'green' ? 'bg-green-100 text-green-700' :
                                  status.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {status.text}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {files.length > 50 && (
                          <div className="text-center text-sm text-gray-500 py-4">
                            ... and {files.length - 50} more files (showing first 50)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Credit Validation Info - Show when files are selected AND album not created yet */}
              {files.length > 0 && !albumCreated && (
                <div className="mb-6">
                  {(() => {
                    // PRIORITY 1: Check membership expired first
                    if (membershipExpired) {
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
              </div>
                            <div className="flex-1">
                              <p className="font-medium text-red-800">Membership Expired</p>
                              <p className="text-sm text-red-600">
                                Your membership has expired. Renew to use your credits.
                              </p>
              </div>
                            <button
                              onClick={() => navigate('/pricing')}
                              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Renew Membership
                            </button>
              </div>
            </div>
                      );
                    }

                    // PRIORITY 2: Check insufficient credits
                    // REMOVED: Warning moved to AI Settings sidebar
                    if (!hasEnoughCredits) {
                      return null; // Cost preview and warning now in AI Settings
                    }

                    // PRIORITY 3: SUCCESS - Only show if ALL conditions are met
                    // REMOVED: Old cost breakdown moved to AI Settings sidebar
                    if (hasEnoughCredits && !membershipExpired) {
                      return null; // Cost preview now in AI Settings
                    }

                    // Fallback: No credits at all
                    return null; // Handled in AI Settings
                  })()}
            </div>
          )}

              {/* Upload Area - Hide when album is created */}
              {!albumCreated && (
                <div className="mb-10">
                <div
                  className={`relative border-2 border-dashed rounded-xl p-16 text-center transition-all duration-300 ${
                    isDragOver
                      ? 'border-blue-500 bg-blue-50 scale-[1.02] shadow-xl ring-4 ring-blue-100'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50/50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {/* Drag Overlay */}
                  {isDragOver && (
                    <div className="absolute inset-0 flex items-center justify-center bg-blue-500/10 rounded-xl pointer-events-none">
                      <div className="text-center animate-pulse">
                        <div className="text-6xl mb-4">📁</div>
                        <p className="text-2xl font-bold text-blue-600">Drop files here!</p>
                        <p className="text-blue-500 mt-2">Release to upload</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    {/* Upload Icon */}
                    <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>

                    {/* Text */}
                    <div>
                      <p className="text-xl font-medium text-gray-900 mb-1">
                        {isDragOver ? 'Drop files here' : 'Upload images'}
                      </p>
                      <p className="text-gray-500 mb-4">
                        Drag and drop files here, or{' '}
                        <button className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2">
                          browse files
                  </button>
                      </p>

                      {/* File Types */}
                      <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>JPEG</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span>PNG</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span>WebP</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                          <span>SVG</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span>≤10MB</span>
                        </div>
                </div>

                      {/* Upload Limit Info */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="text-center space-y-2">
                          <p className="text-sm font-semibold text-gray-900">
                            Max number of files per upload: <span className="text-blue-600">100 files</span>
                          </p>
                        </div>
                      </div>
              </div>
          </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>

                {/* Error Messages - Show when files are selected AND album not created yet */}
                {errorMessages.length > 0 && files.length > 0 && !albumCreated && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
                        Validation Messages ({errorMessages.length})
                      </h3>
                      <button
                        onClick={handleClearErrors}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {errorMessages.map((error) => (
                        <div
                          key={error.id}
                          className={`flex items-start justify-between p-3 rounded-lg border ${
                            error.type === 'error'
                              ? 'bg-red-50 border-red-200 text-red-800'
                              : 'bg-amber-50 border-amber-200 text-amber-800'
                          }`}
                        >
                          <div className="flex items-start space-x-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              error.type === 'error' ? 'text-red-500' : 'text-amber-500'
                            }`} />
                            <p className="text-sm">{error.message}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveError(error.id)}
                            className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File List - Show when files are selected */}
                {files.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Selected Files ({files.length})
                      </h3>
                      <button
                        onClick={() => setFiles([])}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="text-gray-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={file.file.name}>
                                {truncateFilename(file.file.name)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(file.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50"
                            title="Remove this file"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              )}

            </div>

            {/* Sidebar - Responsive: full width on mobile, 4 columns on desktop - Only show when album NOT created */}
            {!albumCreated && (
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-8">
                {/* AI Settings Sidebar - Show when album not created */}
                <AISettingsV2
                  settings={aiSettings}
                  onSettingsChange={setAiSettings}
                  onResetSettings={handleResetAISettings}
                  filesCount={files.length}
                  onUpload={startUpload}
                  isLoading={isCreditValidating}
                  hasEnoughCredits={hasEnoughCredits}
                  requiredCredits={requiredCredits}
                  availableCredits={availableCredits}
                  onEmailNotificationChange={handleEmailNotificationChange}
                  className="bg-white border border-gray-200 rounded-lg p-4 lg:p-6"
                />
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

    </Layout>
  );
};

export default UploadV2Page;